import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '@manager-app/service/error-handler.service';
import { PriceService } from '@manager-app/storage/service/price.service';
import { HcToastrService } from '@manager-app/service/toastr.service';
import { ComponentType, HxAuthService, HxStoreProductService, HxStoreService, HxUiLabelPipe, ProductInfoModel, StoreFullModel, StoreProductModel } from 'hx-services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgIf } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { PriceNewComponent } from '../price-new/price-new.component';

@Component({
    selector: 'app-store-products-new',
    templateUrl: './store-products-new.component.html',
    styleUrls: ['./store-products-new.component.css'],
    imports: [NgIf, FormsModule, ReactiveFormsModule, NgxMaskDirective, PriceNewComponent, TranslocoPipe, HxUiLabelPipe]
})
export class StoreProductsNewComponent implements OnInit {
  @ViewChild('templateRef') public templateRef: TemplateRef<any>;
  @Output() update = new EventEmitter<boolean>();
  private _id: number;
  @Input()
  set id(id) {
    this._id = id || null;
    if (id) {
      this.getItem();
    }
  }

  get id() {
    return this._id;
  }

  store: StoreFullModel;
  unitForm: UntypedFormGroup;
  isLoading = {
    form: false
  };
  isEdit = false;
  // Количество редактируется только пользователями с админским (adm) доступом
  isAdmin = false;
  categories = [];
  product: {
    productInfo?: ProductInfoModel
  } = {};

  constructor(
    private modal: NgbModal,
    private fb: UntypedFormBuilder,
    private storeProductService: HxStoreProductService,
    private priceService: PriceService,
    private storeService: HxStoreService,
    private auth: HxAuthService,
    private toastr: HcToastrService,
    private errorService: ErrorHandlerService,
    private tr: TranslocoService
  ) {
  }

  ngOnInit() {
    this.isAdmin = this.hasAdminAccess();
    this.createForm();
  }

  showModal() {
    this.isEdit = false;
    this.addItem();
  }

  closeModal(modal) {
    this.update.emit(false);
    this.unitForm.reset();

    if (modal) {
      modal.dismiss();
    }
  }

  onSubmitForm(modal) {
    const obj = this.unitForm.getRawValue();
    if (obj.minOrderDay === '') {
      obj.minOrderDay = undefined;
    }
    if (obj.untilOrderTime === '') {
      obj.untilOrderTime = undefined;
    }
    if (obj.receiveAfterTime === '') {
      obj.receiveAfterTime = undefined;
    }
    this.storeProductService.saveStoreProduct(obj).then(() => {
      this.update.emit(true);
      this.toastr.success(this.tr.translate('store-products-new.ts.dataSuccess'));
      modal.dismiss();
    }).catch(err => this.errorService.handler(err.error));
  }

  private getItem() {
    this.isLoading.form = true;
    setTimeout(() => {
      this.addItem();
    }, 0);
    this.loadData();
  }

  private loadData() {
    this.isLoading.form = true;

    this.storeProductService.getStoreProduct(this.id).subscribe(result => {
      if (result) {
        this.updateForm(result);
      }
      this.product = result || {};
      this.isLoading.form = false;
    }, err => {
      this.toastr.error(this.tr.translate('store-products-new.ts.again'));
      this.isLoading.form = false;
    });
  }

  private updateForm(storeProduct: StoreProductModel) {
    this.loadStore(storeProduct.store.id);
    this.isEdit = true;
    this.unitForm.patchValue({
      productInfoId: storeProduct.productInfo.id,
      storeId: storeProduct.store.id,
      organizationId: storeProduct.store.organizationId,
      amount: storeProduct.amount,
      forSale: storeProduct.forSale,
      onlineSellable: storeProduct.onlineSellable,
      minOrderDay: storeProduct.minOrderDay,
      untilOrderTime: storeProduct.untilOrderTime,
      receiveAfterTime: storeProduct.receiveAfterTime,
    });
  }

  private createForm(): void {
    this.unitForm = this.fb.group({
      productInfoId: [''],
      storeId: [''],
      organizationId: [''],
      amount: [{ value: '', disabled: !this.isAdmin }],
      forSale: [false],
      onlineSellable: [false],
      minOrderDay: [null],
      untilOrderTime: [null],
      receiveAfterTime: [null]
    });
  }

  private addItem() {
    this.modal.open(this.templateRef, {size: 'lg'}).result.then(result => {
      this.closeModal(result);
    }).catch(err => {
      this.closeModal(err);
    });
  }

  private hasAdminAccess(): boolean {
    return (this.auth.user?.memberships ?? [])
      .some(m => m.enabled && !m.removed && (m.components ?? []).includes(ComponentType.adm));
  }

  private loadStore(id) {
    if (!id) {
      return this.toastr.warning(this.tr.translate('store-products-new.ts.infoProduct'));
    }

    this.storeService.getFullStore(id).then(store => {
      this.store = store;
    });
  }
}
